import { useCallback, useEffect, useRef, useState } from 'react'
import { KeyRound, Loader2 } from 'lucide-react'
import { api } from '../lib/api'

type Phase = 'checking' | 'locked' | 'open'

/** Remove `?pw=` so the password never lingers in the address bar or history. */
function stripPasswordFromUrl() {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('pw')) return
  url.searchParams.delete('pw')
  const query = url.searchParams.toString()
  window.history.replaceState({}, '', `${url.pathname}${query ? `?${query}` : ''}${url.hash}`)
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<Phase>('checking')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const started = useRef(false)

  const signIn = useCallback(async (pw: string) => {
    setSubmitting(true)
    setError(null)
    try {
      await api.authenticate(pw)
      setPhase('open')
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in')
      setPhase('locked')
      return false
    } finally {
      setSubmitting(false)
    }
  }, [])

  useEffect(() => {
    if (started.current) return
    started.current = true

    const fromUrl = new URLSearchParams(window.location.search).get('pw')

    void (async () => {
      try {
        await api.session()
        setPhase('open')
      } catch {
        if (fromUrl) await signIn(fromUrl)
        else setPhase('locked')
      } finally {
        stripPasswordFromUrl()
      }
    })()
  }, [signIn])

  if (phase === 'open') return <>{children}</>

  if (phase === 'checking') {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loader2 className="size-6 animate-spin text-teal-400" aria-label="Loading" />
      </div>
    )
  }

  return (
    <div className="grid min-h-dvh place-items-center px-6">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void signIn(password)
        }}
        className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-sm ring-1 ring-cream-200"
      >
        <img src="/lantern-logo.svg" alt="Lantern" className="h-8" />
        <h1 className="mt-6 text-lg font-semibold text-ink">Lantern TODOs</h1>
        <p className="mt-1 text-sm text-muted">Enter the team password to get in.</p>

        <div className="relative mt-5">
          <KeyRound className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint" />
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            aria-label="Password"
            className="field pl-9"
          />
        </div>

        {error && (
          <p role="alert" className="mt-3 text-sm text-flame">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !password}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-teal px-4 py-2.5
                     text-sm font-semibold text-white transition hover:bg-teal-700
                     disabled:cursor-not-allowed disabled:opacity-40"
        >
          {submitting && <Loader2 className="size-4 animate-spin" />}
          Let me in
        </button>
      </form>
    </div>
  )
}
